import { v4 as uuid } from 'uuid';
import env from '../env';
import * as ResponseHeaders from '../constants/response-headers';
import * as MobileAppWebViewTypes from '../constants/mobile-app/web-view';
import * as LocalStorageKeyKind from '../constants/local-storage-key-kind';
import isMobileApp from '../utils/mobile-app/is-mobile-app';
import sendMobileAppEvent from '../utils/mobile-app/send-event';
import getMobileAppRequestHeaders from '../utils/mobile-app/get-request-headers';
import { parseStepUpMessage } from './utils';
import { STEP_UP_ERROR_NAME } from '../constants/response-headers';
import { sentryFetchHandler, sentryFetchExceptionHandler } from '../sentry/sentryFetchHandler';
import { buildUrlWithParams } from '../utils/url';
const getHeaders = ({
  headers,
  autoContentType,
  includeCorrelationId = true,
  includeMobileHeaders = true
}) => {
  const baseRequestHeaders = {
    Accept: 'application/json'
  };
  if (!autoContentType) baseRequestHeaders['Content-Type'] = 'application/json';
  if (includeCorrelationId) {
    baseRequestHeaders['X-CF-CORR-ID'] = uuid();
    baseRequestHeaders['X-CF-SOURCE-ID'] = env.REACT_APP_HEADER_SOURCE_ID;
  }
  return {
    ...baseRequestHeaders,
    ...headers,
    ...(includeMobileHeaders ? getMobileAppRequestHeaders() : {})
  };
};
const getUrl = ({
  url,
  params,
  baseUrl = env.REACT_APP_API_URL
}) => {
  const useBaseUrl = env.NODE_ENV === 'production' || !env.USE_API_PROXY;
  const base = useBaseUrl ? baseUrl || '' : '';
  const requestUrl = `${base}${url}`;
  const fullUrl = buildUrlWithParams(requestUrl, params);
  return fullUrl;
};
const getBody = ({
  data,
  serializeBody
}) => {
  if (!data) return undefined;
  if (serializeBody) return JSON.stringify(data);
  return data;
};
const parseResponse = async ({
  url,
  response,
  parseJson
}) => {
  let data = null;
  try {
    data = await (parseJson ? response.json() : response.text());
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn(`Could not parse response coming from ${url}`, response);
  }
  return parseJson ? data : {
    message: data
  };
};

/**
 * @typedef {Object} FetchJsonOptions
 * @property {string} url The URL to fetch
 * @property {string} baseUrl The base URL to use for the request
 * @property {string} [method] The HTTP method to use for the request
 * @property {Object} [headers] The headers to use for the request
 * @property {Object} [params] The query parameters
 * @property {Object} data The body data
 * @property {boolean} [parseJson] Whether to parse the response as JSON
 * @property {boolean} [reportError] Whether to report the error to Sentry
 * @property {boolean} [autoContentType] Whether to automatically set the Content-Type header
 * @property {RequestCredentials} [credentials] The credentials to use for the request
 * @property {boolean} [includeCorrelationId] Whether to include the correlation ID in the request headers
 * @property {boolean} [includeMobileHeaders] Whether to include the mobile app headers
 * @property {boolean} [serializeBody=true] Whether to call JSON.stringify on the data that is passed as the request body
 * @property {Function} onError The callback to call if the request fails
 * @property {Function} onStepUp The callback to call if the request requires a step up
 */

/**
 * @typedef {Object} FetchJsonResult
 * @property {null|Object|boolean} error Whether the request failed. Can be `null`, an error object, or a boolean.
 * @property {unknown} [exception] Whether there's an exception while fetching. If so, `error` will be `true`.
 * @property {number} [status] The status code of the response, if the browser was able to fulfill the request.
 * @property {Object} [data] The response data
 * @property {Response} [response] The response object, if the browser was able to fulfill the request.
 * @property {Request} request The request object
 */

/**
 * @callback FetchJson
 * @param {FetchJsonOptions} options
 * @returns {Promise<FetchJsonResult>}
 */

/**
 * @type {FetchJson}
 */
const fetchJson = async options => {
  const {
    url,
    baseUrl,
    method = 'get',
    headers = {},
    params = {},
    data,
    parseJson = true,
    reportError = true,
    autoContentType = false,
    credentials = 'include',
    includeCorrelationId = true,
    includeMobileHeaders = true,
    serializeBody = true,
    onError,
    onStepUp,
    ...restOfOptions
  } = options;
  const requestHeaders = getHeaders({
    headers,
    autoContentType,
    includeCorrelationId,
    includeMobileHeaders
  });
  const requestUrl = getUrl({
    url,
    params,
    baseUrl
  });
  const body = getBody({
    data,
    serializeBody
  });
  const request = new Request(requestUrl, {
    credentials,
    method,
    body,
    ...restOfOptions,
    headers: requestHeaders
  });
  let response;
  try {
    response = await fetch(request);
  } catch (exception) {
    if (reportError) {
      sentryFetchExceptionHandler({
        request,
        exception
      });
    }
    return {
      error: true,
      exception
    };
  }
  const responseHeaderSessionChanged = response.headers.get(ResponseHeaders.CF_SESSION_CHANGED);
  if (responseHeaderSessionChanged) {
    global?.localStorage?.setItem(LocalStorageKeyKind.CLIENT_USER_AUTHENTICATED, 'true');
  }
  if (isMobileApp() && responseHeaderSessionChanged) {
    try {
      sendMobileAppEvent({
        type: MobileAppWebViewTypes.AUTH_SESSION_CHANGED
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  }
  const responseData = await parseResponse({
    url,
    response,
    parseJson
  });
  const {
    status,
    ok
  } = response;
  const error = ok ? null : responseData || !ok;
  const result = {
    error,
    status,
    data: responseData,
    response,
    request
  };
  const stepUp = error?.codeName === STEP_UP_ERROR_NAME ? parseStepUpMessage({
    message: error?.message
  }) : undefined;
  // A step up scenario will be an error, but we want to handle it before the error and replay the fetch
  if (stepUp && onStepUp) {
    try {
      await onStepUp({
        ...result,
        stepUp
      });
      // If onStepUp is successful, "replay" the fetchJson
      return fetchJson(options);
    } catch (e) {
      // set the stepUpError flag to true so that fetch-adn-receive can handle it
      result.stepUpError = true;
    }
  }
  if (error) {
    // Log all fetch errors to Sentry
    if (reportError) sentryFetchHandler(result);

    // Allow custom onError callbacks by the apps
    if (onError) onError(result);
  }
  return result;
};
export default fetchJson;

/**
 * @type {FetchJson}
 */
export const deleteJson = options => fetchJson({
  method: 'delete',
  ...options
});
/**
 * @type {FetchJson}
 */
export const postJson = options => fetchJson({
  method: 'post',
  ...options
});
/**
 * @type {FetchJson}
 */
export const patchJson = options => fetchJson({
  method: 'PATCH',
  ...options
});
/**
 * @type {FetchJson}
 */
export const putJson = options => fetchJson({
  method: 'put',
  ...options
});

/**
 * @typedef {Object} FetchJsonMethods
 * @property {FetchJson} fetchJson - Performs a generic fetch request.
 * @property {FetchJson} postJson - Performs a POST request.
 * @property {FetchJson} putJson - Performs a PUT request.
 * @property {FetchJson} patchJson - Performs a PATCH request.
 * @property {FetchJson} deleteJson - Performs a DELETE request.
 */

/**
 * @type {FetchJsonMethods}
 */
export const methods = {
  fetchJson,
  postJson,
  putJson,
  patchJson,
  deleteJson
};

/**
 * Used to wrap functions to use default options. Can be used with an object or a function that takes the original options and returns a new object.
 * Usage: const methods = withOptions({ headers: { 'X-CF-SOURCE-ID': 'funnel-ui' }, onStepUp: () => {} });
 * @deprecated Use `applyMiddlewares` from `@upgrade/ui-utils/api/fetch/apply-middlewares` instead.
 * @param {FetchJsonOptions|function(FetchJsonOptions): FetchJsonOptions} options
 * @returns {FetchJsonMethods}
 */
export const withOptions = options => {
  const optionsWrapper = opt => typeof options === 'function' ? options(opt) : {
    ...options,
    ...opt
  };
  return Object.keys(methods).reduce((acc, key) => {
    const method = methods[key];
    return {
      ...acc,
      [key]: opt => method(optionsWrapper(opt))
    };
  }, {});
};
//# sourceMappingURL=fetch-json.js.map